import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  NavigationStart,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { filter, Observable, take, tap } from 'rxjs';
import { Select, Store as NgxsStore } from '@ngxs/store';
import { Store } from '@ngrx/store';
import { DrawerControllerService } from '../../services/drawer-controller.service';
import { ApplicationState } from '../../_state/app.state';
import {
  SelectMenuItem,
  ToggleMobileMenu,
  ToggleSidebarMenu,
  ToggleSubMenu
} from '../../_state/app.actions';
import { MenuItem } from '@app/core/models';
import { menuItems } from '@app/core/constants';
import { appFeature } from '@app/core/store/app.reducer';
import { CommonModule } from '@angular/common';
import {
  MatDrawerContainer,
  MatDrawerContent
} from '@angular/material/sidenav';
import { HeaderComponent } from '@app/components/header/header.component';
import { map } from 'rxjs/operators';
import { RoleName } from '@app/shared/enums/user.enum';
import { RoleFunctionName } from '@app/core/enums/role-function-name.enum';
import { AccessMatrixLevel } from '@app/core/enums/access-settings.enums';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton, MatIconButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import { NotificationAlertComponent } from '@app/pages/notifications/components/notification-alert/notification-alert.component';
import { NotificationsStore } from '@app/pages/notifications/store/notifications.store';
import { NotificationsHubService } from '@app/pages/notifications/services/notification-hub.service';
import { MatDialog } from '@angular/material/dialog';
import { InstructionForSupplierModalComponent } from '@app/components/layout/components/instruction-for-supplier-modal/instruction-for-supplier-modal.component';
import { OAuthService } from 'angular-oauth2-oidc';
import { BreadcrumbsComponent } from '@app/shared/components/breadcrumbs/breadcrumbs.component';
import { AccountHubService } from '@app/core/hubs/account-hub/account-hub.service';
import { UserService } from '@app/services/user.service';
import { CommunicationChartPanelStore } from '@app/pages/constructor/constructor-components/communication-chart/store/communication-chart-panel.store';
import { CommunicationChartPanelHubService } from '@app/pages/constructor/constructor-components/communication-chart/services/communication-chart-panel-hub.service';
import { CommunicationChartAlertComponent } from '@app/pages/constructor/constructor-components/communication-chart/components/communication-chart-alert/communication-chart-alert.component';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  imports: [
    CommonModule,
    MatDrawerContainer,
    MatDrawerContent,
    RouterOutlet,
    HeaderComponent,
    RouterLink,
    RouterLinkActive,
    MatTooltip,
    MatButton,
    MatIconButton,
    NotificationAlertComponent,
    TranslatePipe,
    BreadcrumbsComponent,
    CommunicationChartAlertComponent
  ],
  providers: [
    NotificationsStore,
    CommunicationChartPanelStore,
    CommunicationChartPanelHubService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LayoutComponent implements OnInit, OnDestroy {
  @Select(ApplicationState.submenuOpened) submenuOpened$: Observable<boolean>;
  @Select(ApplicationState.selectedMenuItem)
  selectedMenuItem$: Observable<MenuItem | null>;
  @Select(ApplicationState.isOpenSidebarMenu)
  isOpenSidebarMenu$: Observable<boolean>;
  @Select(ApplicationState.isOpenMobileMenu)
  isOpenMobileMenu$: Observable<boolean>;

  public user$ = this.store.select(appFeature.selectUser);

  public isDrawerOpened = false;
  public openedSubGroups: { [key: number]: boolean } = {};
  public isWhiteBg = false;
  public isMobile = false;

  public readonly menuItems = menuItems;
  public readonly urlForOtherColorPage = [
    '/procurement/tender-auction/tender-journal',
    'procurement/tender-auction/approval-tasks',
    '/procurement/pr/pr-list',
    '/procurement/tender-auction/approval-tasks',
    '/procurement/supplier-management/suppliers-list'
  ];

  public isCurrentTitleOpenedMenu: string;
  public userId: string;
  public companyId: string;

  private userRoles: RoleName[] = [];
  private userFunctions: Partial<
    Record<RoleFunctionName, AccessMatrixLevel>
  > = {};

  private readonly LEVEL_ORDER: Record<AccessMatrixLevel, number> = {
    [AccessMatrixLevel.NoAccess]: 0,
    [AccessMatrixLevel.Review]: 1,
    [AccessMatrixLevel.Edit]: 2
  };

  constructor(
    private router: Router,
    private drawerController: DrawerControllerService,
    private ngxsStore: NgxsStore,
    private store: Store,
    private destroyRef: DestroyRef,
    private notificationsStore: NotificationsStore,
    private notificationsHubService: NotificationsHubService,
    private communicationChartPanelStore: CommunicationChartPanelStore,
    private communicationChartPanelHubService: CommunicationChartPanelHubService,
    private matDialog: MatDialog,
    private oauthService: OAuthService,
    private accountHub: AccountHubService,
    private userService: UserService
  ) {}

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.isMobile = screen.width <= 620;
    if (window.innerWidth >= 620) {
      this.ngxsStore.dispatch(new ToggleSidebarMenu(true));
      this.ngxsStore.dispatch(new ToggleMobileMenu(false));
    }
  }

  ngOnInit(): void {
    this.initializeIsDrawerData();
    this.streamRouterEventsData();
    this.streamRouterUrlForChangeColor();
    this.streamSelectedMenuItem();
    this.getUserRoles();
    this.startStreamToNotificationData();
    this.startStreamToCommunicationChartData();

    this.isMobile = screen.width <= 620;
    if (window.innerWidth >= 620) {
      this.ngxsStore.dispatch(new ToggleSidebarMenu(true));
      this.ngxsStore.dispatch(new ToggleMobileMenu(false));
    }

    this.checkToCurrentOpenPage();
  }

  ngOnDestroy(): void {
    if (
      !window.location.href.includes(
        'procurement/suppliers-office/procurement-list'
      ) &&
      this.oauthService.hasValidAccessToken()
    ) {
      this.notificationsHubService.stopHub();
      this.accountHub.stopHub();
    }
  }

  public hasRequiredRole(requiredRoles?: RoleName[]): boolean {
    if (!requiredRoles) return true;

    const allowedRoles = [...requiredRoles];
    return this.userRoles.some(userRole => allowedRoles.includes(userRole));
  }

  public hasFunctionAccess(
    requiredFunctions?: RoleFunctionName[]
  ): boolean {
    if (!requiredFunctions || requiredFunctions.length === 0) return true;

    return requiredFunctions.some(fn => {
      const level = this.userFunctions[fn];
      return (
        level !== undefined &&
        this.LEVEL_ORDER[level] >= this.LEVEL_ORDER[AccessMatrixLevel.Review]
      );
    });
  }

  public openInstructionForSupplierModal(): void {
    this.matDialog.open(InstructionForSupplierModalComponent);
  }

  public goToLink(url: string) {
    window.open(url, '_blank');
  }

  public closeMobileMenu(submenuOpened: boolean): void {
    this.ngxsStore.dispatch(new ToggleMobileMenu(submenuOpened));
  }

  public toggleSubMenu(isOpenSubMenu?: boolean): void {
    if (isOpenSubMenu!) {
      this.ngxsStore.dispatch(new ToggleSubMenu());
    } else {
      this.ngxsStore.dispatch(new ToggleSubMenu());
      if (window.innerWidth <= 620) {
        this.ngxsStore.dispatch(new ToggleSidebarMenu(true));
      }
    }
  }

  public selectMenuItem(
    menuItem: MenuItem,
    subMenuOpened?: boolean | null
  ): void {
    if (this.isCurrentTitleOpenedMenu !== menuItem.title) {
      this.isCurrentTitleOpenedMenu = menuItem.title;
      this.ngxsStore.dispatch(new SelectMenuItem(menuItem, true));
    } else {
      this.ngxsStore.dispatch(new SelectMenuItem(menuItem, !subMenuOpened));
    }

    if (!menuItem.subMenu && innerWidth <= 620) {
      this.ngxsStore.dispatch(new ToggleMobileMenu(false));
    }

    if (
      this.userRoles.includes(RoleName.Supplier) &&
      menuItem.routerLink === '/procurement'
    ) {
      this.router.navigate(['/procurement/suppliers-office/procurement-list']);
      this.ngxsStore.dispatch(new SelectMenuItem(menuItem, !subMenuOpened));
    }
  }

  public toggleSubGroup(index: number): void {
    this.openedSubGroups[index] = !this.openedSubGroups[index];
  }

  private initializeIsDrawerData(): void {
    this.drawerController
      .getDrawerState()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        this.isDrawerOpened = val;
      });
  }

  private streamRouterEventsData(): void {
    const foundMenuItem = menuItems.find(menuItem => {
      this.router.url.startsWith(menuItem.routerLink);
    });

    if (foundMenuItem) {
      this.selectMenuItem(foundMenuItem);
    }
  }

  private streamRouterUrlForChangeColor(): void {
    this.isWhiteBg = this.urlForOtherColorPage.includes(this.router.url);

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationStart) => {
        this.isWhiteBg = this.urlForOtherColorPage.includes(event.url);
      });
  }

  private streamSelectedMenuItem(): void {
    this.selectedMenuItem$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.openedSubGroups = {};
        })
      )
      .subscribe();
  }

  private getUserRoles(): void {
    this.user$
      .pipe(
        filter(Boolean),
        map(user => ({
          roles: user.roles.map(item => item.name),
          functionNames: user.functionNames ?? {}
        }))
      )
      .subscribe(({ roles, functionNames }) => {
        this.userRoles = roles;
        this.userFunctions = functionNames;
      });
  }

  private startStreamToNotificationData(): void {
    this.user$.pipe(take(1), filter(Boolean)).subscribe(user => {
      this.userId = user.id;
      this.companyId = user.companyId;
      this.notificationsStore.getNotificationsLists({
        userId: this.userId
      });
      this.notificationsStore.getCountUnread(this.userId);
    });

    if (
      window.location.href.includes(
        'procurement/suppliers-office/procurement-list'
      ) &&
      !this.oauthService.hasValidAccessToken()
    ) {
      return;
    }

    if (!this.accountHub.isHubConnected) {
      this.connectAccountHub();
    }

    this.notificationsHubService.initHub();
    this.notificationsHubService.streamReceivedData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.notificationsStore.setNotificationAlertList(data);
      });
  }

  private startStreamToCommunicationChartData(): void {
    this.communicationChartPanelHubService.initHub();
    this.communicationChartPanelHubService.streamReceivedData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.communicationChartPanelStore.setCommunicationChartAlert(data);
      });
  }

  private connectAccountHub(): void {
    this.accountHub.initHub();

    this.accountHub.hubConnectionStarted
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.accountHub.joinGroup(this.companyId);
      });

    this.accountHub.streamReceivedData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.userService
          .getUser()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      });
  }

  private checkToCurrentOpenPage(): void {
    const currentUrl = this.router.url.split('/')[1];
    const currentPage = this.menuItems.find(data =>
      data.routerLink.includes(currentUrl)
    );

    const skipSelectPages = ['main', 'notifications', 'marketplace'];

    if (currentPage && !skipSelectPages.includes(currentUrl)) {
      this.ngxsStore.dispatch(new SelectMenuItem(currentPage, false));
    } else if (currentPage) {
      this.ngxsStore.dispatch(new SelectMenuItem(currentPage, true));
    }
  }
}
